import React from 'react';
import { shortenHash } from '@soundxyz/utils/const';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { CreditRoleType } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { SectionTitle } from '../shared';
import { SplitsContent } from './SplitsContent';
import {
  AVATAR_SIZE,
  AvatarContainer,
  Container,
  Name,
  NameContainer,
  Percent,
  SplitsContentCredits,
  SplitsContentTitle,
  SplitUser,
} from './shared';

type SectionSplits = Array<{
  ownerAddress: string;
  avatarUrl?: string | null;
  percent: number;
  roles: Array<CreditRoleType>;
  owner: {
    id: string;
    username: string;
    artist?: {
      id: string;
    } | null;
    webappUri: string;
  };
}>;

const getSection = (
  creditAllocations: SectionSplits,
  /** A user can be have multiple roles. We show percentage once on their first category but we we want to show the name in all categories. */
  prevCreditAllocations: SectionSplits = [],
) => {
  const notDisplayedYet = creditAllocations.filter(
    item => !prevCreditAllocations?.find(prev => prev.ownerAddress === item.ownerAddress),
  );

  const alreadyDisplayed = creditAllocations.filter(item => {
    return prevCreditAllocations?.find(prev => prev.ownerAddress === item.ownerAddress);
  });

  const sorted = notDisplayedYet.sort((a, b) => b.percent - a.percent);

  const combinedAllocations = [...sorted, ...alreadyDisplayed];

  return combinedAllocations.length
    ? combinedAllocations.map((creditData, i) => {
        const owner = creditData.owner;

        const avatarUrl = creditData.avatarUrl;
        const username = owner?.username;

        const isPublicAddress = username === creditData.ownerAddress;

        const alreadyDisplayed = !!prevCreditAllocations?.find(
          prev => prev.ownerAddress === creditData.ownerAddress,
        );

        const hasArtistRole = !!owner?.artist?.id;
        return (
          <SplitUser key={`${creditData.ownerAddress}-${creditData.roles}-${i}`}>
            <AvatarContainer>
              <Avatar
                src={avatarUrl}
                size={AVATAR_SIZE}
                username={username}
                walletAddress={creditData.ownerAddress}
                borderRadius={6}
                hasShadow
                webappUri={owner.webappUri}
              />
              <NameContainer>
                <AccessibleLink pathnameUri={webappUriPath(owner.webappUri)}>
                  <Name>{isPublicAddress ? shortenHash(username, 5, 4) : username}</Name>
                </AccessibleLink>
                {hasArtistRole && <VerifiedTooltip />}
              </NameContainer>
            </AvatarContainer>
            {/*
              We encountered a UI issue where splits percentages were displayed with too far decimal points.
              Since splits are clamped to 0.01%, we should round to at most 2 decimal points to avoid further issues.
             */}
            {!alreadyDisplayed && creditData.percent && (
              <Percent>{Math.round(creditData.percent * 100) / 100}%</Percent>
            )}
          </SplitUser>
        );
      })
    : '—';
};

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2513/add-tests-for-creditsplitssection
export const CreditSplitsSection = ({ splits }: { splits: SectionSplits }) => {
  const artists = splits.filter(data => data.roles?.includes('ARTIST'));
  const songwriters = splits.filter(data => data.roles?.includes('SONGWRITER'));
  const producers = splits.filter(data => data.roles?.includes('PRODUCER'));
  const curators = splits.filter(data => data.roles?.includes('CURATOR'));
  const remixers = splits.filter(data => data.roles?.includes('REMIXER'));
  const visualArtists = splits.filter(data => data.roles?.includes('VISUAL_ARTIST'));
  const other = splits.filter(data => data.roles?.includes('OTHER'));

  return (
    <Container>
      <SectionTitle title="Credits & Splits" />
      <div>
        <SplitsContent>
          <SplitsContentTitle>Performed by:</SplitsContentTitle>
          <SplitsContentCredits>
            {artists && artists.length > 0 && getSection(artists, [])}
          </SplitsContentCredits>
        </SplitsContent>
        {songwriters.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Written by:</SplitsContentTitle>
            <SplitsContentCredits>{getSection(songwriters, artists)}</SplitsContentCredits>
          </SplitsContent>
        )}
        {producers.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Produced by:</SplitsContentTitle>
            <SplitsContentCredits>
              {getSection(producers, [...artists, ...songwriters])}
            </SplitsContentCredits>
          </SplitsContent>
        )}
        {curators.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Curated by:</SplitsContentTitle>
            <SplitsContentCredits>
              {getSection(curators, [...artists, ...songwriters, ...producers])}
            </SplitsContentCredits>
          </SplitsContent>
        )}
        {remixers.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Remixed by:</SplitsContentTitle>
            <SplitsContentCredits>
              {getSection(remixers, [...artists, ...songwriters, ...producers, ...curators])}
            </SplitsContentCredits>
          </SplitsContent>
        )}
        {visualArtists.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Artwork by:</SplitsContentTitle>
            <SplitsContentCredits>
              {getSection(visualArtists, [
                ...artists,
                ...songwriters,
                ...producers,
                ...curators,
                ...remixers,
              ])}
            </SplitsContentCredits>
          </SplitsContent>
        )}
        {other.length > 0 && (
          <SplitsContent>
            <SplitsContentTitle>Other:</SplitsContentTitle>
            <SplitsContentCredits>
              {getSection(other, [
                ...artists,
                ...songwriters,
                ...producers,
                ...curators,
                ...remixers,
                ...visualArtists,
              ])}
            </SplitsContentCredits>
          </SplitsContent>
        )}
      </div>
    </Container>
  );
};
